import { Outlet, useLocation } from "react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Suspense,
  lazy,
} from "react";
import { saveLastAppRouteIfApplicable } from "../../utils/lastAppRoute";
import { cancelMorningBriefing } from "../../utils/reminderNotifications";
import * as am5 from "@amcharts/amcharts5";
import { MenuProps } from "antd";
import { Toast, Button } from "antd-mobile";
import { UserOutlined } from "@ant-design/icons";
import { FilterOutline } from "antd-mobile-icons";
import classNames from "classnames";
import { amchartLicenseKey } from "../../config";
import { useTranslation } from "react-i18next";
import {
  getTabsWeb,
  getPremiumTabs,
  getSettingsTabs,
  getHealthAppTabs,
} from "./tabs";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  pushNotificationProvider,
  timeProvider,
  electronEngineProvider,
  summaryController,
  webIntercomProvider,
  homeHealthController,
  analyticsController,
  smsController,
} from "../../AppController";
import { useAuth } from "../../hooks/auth";
import { useRouterHook } from "../../hooks/useRouterHook";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { NetworkBar } from "../../components/CustomComponents/NetworkBar";
import { appNotification } from "../../util/appNotification";
import { useScrollTop } from "../../hooks/useScrollTop";
import { useScreenTimeMetrics } from "../../hooks/useScreenTimeMetrics";
import { ScreenLoaderWhite } from "../../components/CustomComponents/ScreenLoaderWhite";
import { useFCMTokenUpdate } from "../../hooks/fcmTokenListener";
import { sendZapierTrigger } from "../../util/zapierMain";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import { useIntercom } from "../../hooks/useIntercom";
import { SearchPatientModal } from "../../components/CustomComponents/SearchPatientModal";
import { useSearchHook } from "./useSearchHook";
import { useScreenTimeout } from "../../hooks/useScreenTimeout";
import { useSiderAutoCollapse } from "../../hooks/useSiderAutoCollapse";
import { useMobileTopBottomColor } from "../../hooks/useMobileTopBottomColor";
import { isPIN } from "../../util/pin";
import { useNativeLocalNotificationDeepLink } from "../../utils/nativeLocalNotificationDeepLink";
import { useAppSelector } from "../../hooks/redux";
import { useAssignedDevicePolling } from "../../hooks/useAssignedDevicePolling";
import { useDeviceOnlinePolling } from "../../hooks/useDeviceOnlinePolling";
import { useDeviceOnlineSseStream } from "../../hooks/useDeviceOnlineSseStream";
import { useNativePushNotificationRouter } from "../../hooks/useNativePushNotificationRouter";
import { useDeviceStateLifecycleTrace } from "../../hooks/useDeviceStateLifecycleTrace";
import { resolvePatientId } from "../../pages/PatientProfile-v3/components/DebugConstants";
import { isCircadiaUser } from "../../utils/circadiaUser";
// Local imports
import styles from "./styles.module.scss";
import { Header as HeaderMobile } from "../../components/LayoutComponents/Header/index.mobile";
import { MobileLeftMenu } from "../../components/LayoutComponents/LeftMenu/index.mobile";
import { Footer } from "../../components/LayoutComponents/Footer/index.mobile";
import { useHook } from "./useHook";

const WellnessCheckSheet = lazy(
  () => import("../../pages/PatientProfile-v3/components/WellnessCheckSheet")
);
const VideoCallModal = lazy(
  () => import("../../components/CustomComponents/VideoCallModal")
);

if (amchartLicenseKey) {
  am5.addLicense(amchartLicenseKey);
}

type MenuItem = Required<MenuProps>["items"][number];

let lastMainPath = "";

const createMenuItems = (
  items: ITabObject[] | ISupportTabObject[],
  collapsed: boolean,
  setRouteActive: (route: string) => void
): MenuItem[] => {
  return items.map((item) => ({
    label: item.title,
    icon: item.icon,
    key: item.key,
    onClick: () => setRouteActive(item.key),
    className: classNames(
      styles.menuItem,
      !collapsed && styles.menuItemCollapsed
    ),
  }));
};

const MobileLayout: FC = () => {
  const { i18n: i18nInstance } = useTranslation();
  const [showWellnessCheck, setShowWellnessCheck] = useState(false);
  const [wellnessVideoCallVisible, setWellnessVideoCallVisible] =
    useState(false);

  const { wellnessPatientId, wellnessUserName, wellnessUserEmail, isFamilyMember } =
    useAppSelector((state) => ({
      wellnessPatientId: resolvePatientId(
        state.settings.notifications?.patient_id
      ),
      wellnessUserName:
        state.auth.user?.firstName ||
        state.auth.user?.fullName?.split(" ")[0] ||
        state.auth.user?.email?.split("@")[0] ||
        "Patient",
      wellnessUserEmail: state.auth.user?.email,
      isFamilyMember: state.auth.user?.is_family_member === true,
    }));

  // Center microphone tab: shown for linked family-member logins, and for
  // Circadia-internal staff (email domain) while the feature is in development.
  const showVoiceTab = isFamilyMember || isCircadiaUser(wellnessUserEmail);

  const currentHealthAppTabs = useMemo(
    () => getHealthAppTabs({ showVoice: showVoiceTab }),
    [i18nInstance.language, showVoiceTab]
  );

  useMobileTopBottomColor({
    circadiaEngineProvider: circadiaEngineProvider,
  });
  useAssignedDevicePolling();
  useDeviceOnlinePolling();
  useDeviceOnlineSseStream();
  useDeviceStateLifecycleTrace();
  useFCMTokenUpdate({
    electronEngineProvider: electronEngineProvider,
    pushNotificationProvider: pushNotificationProvider,
  });
  useNativeLocalNotificationDeepLink();
  useNativePushNotificationRouter();
  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
  });
  const location = useLocation();

  const {
    isSearchPatientModalOpen,
    onSearchPatientModalOpen: _onSearchPatientModalOpen,
    onSearchPatientModalClose,
    searchValue,
    onChange,
    response,
  } = useSearchHook();
  const {
    menuOpen,
    user,
    userClinic,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    showFooter,
    onRightIconClick,
    uniquePageKey,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    topRightIcon,
    connectionStatus,
    showLoading,
    onPatientClick,
    refreshSummary,
    onSetupClick,
  } = useHook({
    analyticsController,
    location,
    clinicController: clinicController,
    populateAppData,
    summaryController,
    localStorageProvider,
  });

  const { collapsed } = useSiderAutoCollapse();

  useScreenTimeMetrics({ analyticsController });

  // useSummary(10 * 60 * 1000);

  useScreenTimeout({
    localStorageProvider,
    location,
    refreshSummary,
    isPIN,
  });
  const { onLogout } = useAuth({
    authController: authController,
    localStorageProvider: localStorageProvider,
    pushNotificationProvider,
    populateAppData,
    analyticsController,
    appNotification,
    circadiaEngineProvider,
    sendZapierTrigger,
  });

  const _pathname = location.pathname;

  // Persist current route so we can return the user here after Connect Device / User Info etc.
  useEffect(() => {
    saveLastAppRouteIfApplicable(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // Morning briefing is force-disabled app-wide. Cancel any pending slots
  // left over on returning users the moment the authenticated mobile layout
  // mounts, not just when they visit the profile page.
  useEffect(() => {
    cancelMorningBriefing().catch(() => {
      /* non-critical */
    });
  }, []);

  const buttonIcon = (
    <FilterOutline className={styles.actionButtonIconFilter} />
  );

  useScrollTop();

  const navigate = useAppNavigate();

  // First-time Wi-Fi setup gate — temporarily hidden.
  // To re-enable, uncomment the useEffect below.
  // useEffect(() => {
  //   const isSetupPage = location.pathname.includes("setup/wifi");
  //   const isConnectFlow = location.pathname.includes("connect-device");
  //   if (isSetupPage || isConnectFlow) return;
  //   if (localStorage.getItem("circadia_wifi_setup_seen")) return;
  //   if (!circadiaEngineProvider.isWindowCircadia()) return;
  //   circadiaEngineProvider.getWifiCredentials()
  //     .then((creds) => {
  //       if (!creds?.ssid) {
  //         navigate("/setup/wifi");
  //       }
  //     })
  //     .catch(() => {});
  // }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setRouteActive = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  const _items = useMemo(
    () => createMenuItems(getTabsWeb(), collapsed, setRouteActive),
    [collapsed, setRouteActive]
  );
  const _premiumItems = useMemo(
    () => createMenuItems(getPremiumTabs(), collapsed, setRouteActive),
    [collapsed, setRouteActive]
  );

  const handleLogout = useCallback(() => {
    setConfirmLogout(false);
    setTimeout(() => {
      onLogout();
    }, 100);
  }, [onLogout]);

  lastMainPath = getTabsWeb()
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  lastMainPath = getPremiumTabs()
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  lastMainPath = getSettingsTabs()
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  const { handleToggle: _handleToggle } = useIntercom({
    webIntercomProvider: webIntercomProvider,
    circadiaEngineProvider: circadiaEngineProvider,
    analyticsController: analyticsController,
  });

  // Other full-screen routes without header/footer
  if (
    location.pathname.includes("summary") ||
    location.pathname.includes("chime") ||
    // location.pathname.includes("patient-profile") ||
    location.pathname.includes("user-info") ||
    location.pathname.includes("bed-exit-settings") ||
    location.pathname.includes("setup/wifi")
  ) {
    return (
      <div className={styles.app}>
        <div className={styles.body}>
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            <Outlet />
          </AppErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <NetworkBar connectionStatus={connectionStatus} toast={Toast} />
      <>
        <div className={styles.body} id="body">
          {showHeaderBar() && (
            <HeaderMobile
              onClickSearch={onClickSearch}
              rightIcon={
                topRightIcon !== undefined &&
                false && (
                  <div className="headerRightButtonCon">
                    <Button
                      fill="none"
                      onClick={onRightIconClick}
                      data-test-id='"button-filter'
                    >
                      {buttonIcon}
                    </Button>
                  </div>
                )
              }
              leftIcon={
                <span id="menu-icon">
                  {/* <MenuOutlined className="searchIcon" onClick={toggleMenu} /> */}
                  <UserOutlined className="searchIcon" onClick={toggleMenu} />
                </span>
              }
            />
          )}
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
                connectionStatus={connectionStatus}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            {showLoading ? <ScreenLoaderWhite /> : <Outlet />}
          </AppErrorBoundary>
        </div>
        {showFooter() && (
          <div className={styles.bottom}>
            <Footer tabs={currentHealthAppTabs} tapEvent={footerTapEvent} />
          </div>
        )}

        {/* Floating Action Button — wellness check */}
        {/* {showFooter() && (
          <button
            type="button"
            className={styles.fab}
            onClick={() => setShowWellnessCheck(true)}
            aria-label="How are you feeling?"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.1602 22.668C15.8796 22.3788 15.5439 22.149 15.1728 21.9921C14.8017 21.8352 14.4029 21.7545 14 21.7547H13.9453C12.2172 21.7547 10.8227 23.1547 10.8227 24.8773C10.8227 26.6 12.2172 28 13.9453 28H14C15.7281 27.9727 17.1008 26.5508 17.0735 24.8227C17.057 24.0133 16.7289 23.2422 16.1602 22.668ZM19.8297 2.14922C18.4516 0.732812 16.4883 0.0109375 14 0H13.9453C11.9766 0 10.2977 0.535938 8.95236 1.59688C8.31252 2.1 7.74377 2.68516 7.25705 3.33594C6.78673 3.97031 6.40392 4.67031 6.13048 5.41406C6.01017 5.74766 6.1633 6.11406 6.48595 6.25625L9.43361 7.55234C9.77267 7.7 10.1664 7.55234 10.3141 7.21328C10.3195 7.20781 10.3195 7.19688 10.325 7.19141C10.5985 6.52422 11.036 5.86797 11.6266 5.24453C12.1516 4.68672 12.9117 4.41875 13.9453 4.41875H14C15.061 4.42969 15.8539 4.70859 16.4227 5.27734C16.9969 5.85703 17.2758 6.55703 17.2758 7.43203C17.2758 8.16484 17.057 8.8375 16.6086 9.48828C16.1164 10.2047 15.3891 10.9977 14.4485 11.8453C14.2953 11.9875 14.1422 12.1242 14.0055 12.2609C13.1688 13.0813 12.5891 13.8414 12.2445 14.5797C12.0422 15.0117 11.8891 15.5531 11.7797 16.2094C11.6649 16.9805 11.6047 17.757 11.6047 18.5391C11.5992 18.9109 11.8945 19.2117 12.261 19.2227H15.7883C16.1547 19.2227 16.45 18.9328 16.4555 18.5664C16.4828 17.0734 16.6524 16.3461 16.7945 15.9961C16.9313 15.657 17.3742 14.9844 18.7031 13.7047C19.8024 12.6547 20.6227 11.6266 21.1313 10.6531C21.6563 9.64688 21.9242 8.51484 21.9242 7.28984C21.9133 5.30469 21.2133 3.57109 19.8297 2.14922Z"
                fill="white"
              />
            </svg>
          </button>
        )} */}
      </>
      {/* )} */}

      <MobileLeftMenu
        user={user}
        userClinic={userClinic}
        open={menuOpen}
        onToggle={toggleMenu}
        onLogout={onLogoutClick}
        confirmLogout={confirmLogout}
        onCancel={onCancel}
        onConfirmLogout={handleLogout}
        analyticsController={analyticsController}
        circadiaEngineProvider={circadiaEngineProvider}
        localBetaFeature={localBetaFeature}
        onLocalBetaFeature={onLocalBetaFeature}
      />
      <SearchPatientModal
        isOpen={isSearchPatientModalOpen}
        onClose={onSearchPatientModalClose}
        searchValue={searchValue}
        onChange={onChange}
        response={response}
        onPatientClick={onPatientClick}
        onSetupClick={onSetupClick}
      />

      {/* Wellness check sheet — triggered by the raised centre tab-bar button */}
      <Suspense fallback={null}>
        <WellnessCheckSheet
          visible={showWellnessCheck}
          onClose={() => setShowWellnessCheck(false)}
          onNeedHelp={() => {
            setShowWellnessCheck(false);
            setWellnessVideoCallVisible(true);
          }}
        />
      </Suspense>

      <Suspense fallback={null}>
        {wellnessVideoCallVisible && (
          <VideoCallModal
            visible={wellnessVideoCallVisible}
            onClose={() => setWellnessVideoCallVisible(false)}
            patientId={wellnessPatientId?.toString() ?? ""}
            patientName={wellnessUserName}
            userRole="user"
            onCallEnded={() => setWellnessVideoCallVisible(false)}
            userEmail={wellnessUserEmail}
            patientInitiatedCall
          />
        )}
      </Suspense>
    </div>
  );
};

export default MobileLayout;
